import type { InventoryItemDto, InventorySlotDto, User } from '../services/api';

const EXPERIENCE_POTION_KEYS = new Set([
  'SmallExperiencePotion',
  'MediumExperiencePotion',
  'LargeExperiencePotion',
]);

export function isExperiencePotionItemKey(itemTypeKey: string): boolean {
  return EXPERIENCE_POTION_KEYS.has(itemTypeKey);
}

/** ISO from API without offset treated as UTC (same idea as InventoryPage). */
function parseUtcIsoMs(iso: string): number {
  const s = iso.trim();
  const hasZone = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  return Date.parse(hasZone ? s : `${s}Z`);
}

function inventoryItemNotExpired(item: InventoryItemDto): boolean {
  if (!item.expiresAtUtc) return true;
  return parseUtcIsoMs(item.expiresAtUtc) > Date.now();
}

/** True if any slot holds an unused XP potion (not past expiry). */
export function inventorySlotsHaveUnusedXpPotion(slots: InventorySlotDto[]): boolean {
  for (const s of slots) {
    const it = s.item;
    if (!it || !isExperiencePotionItemKey(it.itemTypeKey)) continue;
    if (inventoryItemNotExpired(it)) return true;
  }
  return false;
}

/** Profile XP potion buff still within expiresAtUtc (server may omit stale boosts). */
export function hasActiveXpPotionBuff(user: User | null | undefined): boolean {
  const b = user?.activeXpBoost;
  if (!b?.expiresAtUtc) return false;
  return parseUtcIsoMs(b.expiresAtUtc) > Date.now();
}

/**
 * XP reminder modal: only when there are potions in inventory and no active XP buff from a used potion.
 */
export function shouldPromptXpPotionReminder(
  user: User | null | undefined,
  slots: InventorySlotDto[]
): boolean {
  if (hasActiveXpPotionBuff(user)) return false;
  return inventorySlotsHaveUnusedXpPotion(slots);
}
